import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks';
import { DSNav } from './components';
import { Dashboard } from './features/dashboard';
import { Pipeline, OpportunityDetail } from './features/pipeline';
import { LeadList, LeadDetail } from './features/leads';
import { ActivityList } from './features/activities';
import { AuthCallback } from './features/auth';
import './styles/globals.css';

function LoginPage({ onLogin }: { onLogin: () => void }): ReactNode {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card w-full max-w-md text-center">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">DSCRM</h1>
        <p className="text-gray-500 mb-6">
          Sales pipeline and customer relationship management
        </p>
        <button onClick={onLogin} className="btn btn-primary w-full">
          Sign in with DS Account
        </button>
        <p className="mt-4 text-sm text-gray-400">
          Powered by DigiStratum
        </p>
      </div>
    </div>
  );
}

function AppLayout({ children }: { children: ReactNode }): ReactNode {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex flex-col">
      <DSNav
        user={user}
        onLogout={logout}
        currentAppId="dscrm"
        appName="DSCRM"
      />
      
      {/* App Navigation */}
      <nav className="bg-white border-b px-4">
        <div className="flex gap-1">
          <a
            href="/"
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
          >
            📊 Dashboard
          </a>
          <a
            href="/pipeline"
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
          >
            🎯 Pipeline
          </a>
          <a
            href="/leads"
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
          >
            👤 Leads
          </a>
          <a
            href="/activities"
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
          >
            📋 Activities
          </a>
        </div>
      </nav>

      <main className="flex-1 bg-gray-50">
        {children}
      </main>

      <footer className="py-3 text-center text-sm text-gray-500 border-t bg-white">
        © 2016 - 2026 DigiStratum, LLC
      </footer>
    </div>
  );
}

export function App(): ReactNode {
  const { user, loading, login } = useAuth();

  // Handle auth callback route first (before auth check)
  // This allows the callback to work even when not logged in
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  return (
    <BrowserRouter>
      <AppLayout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/opportunities/:id" element={<OpportunityDetail />} />
          <Route path="/leads" element={<LeadList />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/activities" element={<ActivityList />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
    </BrowserRouter>
  );
}

export default App;
